// El puente nativo serializa cada respuesta completa en el heap Java. Las filas
// históricas se conservan; su tamaño no debe convertirse en un cierre por OOM.
const PREFIX = 512;
const CHUNK = 32768;
const PAGE = 8;

export function createSqliteAccessQueue() {
    let tail = Promise.resolve();
    return task => {
        const result = tail.then(task);
        tail = result.catch(() => undefined);
        return result;
    };
}

export async function boundedSqliteQuery(rawQuery, statement, values, schemas) {
    const sql = statement.trim().replace(/;$/, '');
    const match = /^SELECT\s+\*\s+FROM\s+(\w+)\b/i.exec(sql);
    if (!match || !Object.hasOwn(schemas, match[1])) return rawQuery(statement, values);
    const table = match[1];
    const definition = schemas[table].match(/CREATE TABLE IF NOT EXISTS \w+\s*\(([\s\S]*?)\);/i)?.[1];
    const columns = definition?.split(',').map(part => part.trim().split(/\s+/)[0]);
    if (!columns?.length || columns.some(column => !/^\w+$/.test(column))) throw new Error('Esquema SQLite no verificable.');
    const key = columns.includes('key') ? 'key' : 'id';
    const rows = result => {
        if (!Array.isArray(result?.values)) throw new Error('SQLite no devolvió filas verificables.');
        return result.values;
    };
    const stamp = async () => {
        const version = rows(await rawQuery('PRAGMA data_version;', []))[0]?.data_version;
        const changes = rows(await rawQuery('SELECT total_changes() AS changes;', []))[0]?.changes;
        if (!Number.isSafeInteger(version) || !Number.isSafeInteger(changes)) throw new Error('No se puede comprobar la lectura SQLite.');
        return `${version}:${changes}`;
    };
    const original = await stamp();
    const assertStable = async () => {
        if (await stamp() !== original) throw new Error('El almacenamiento cambió durante la lectura. Se conservan los datos; reintentá la consulta.');
    };
    const projection = columns.flatMap(column => [
        `CASE WHEN typeof(${column})='text' THEN substr(${column},1,${PREFIX}) WHEN typeof(${column}) IN ('null','integer','real') THEN ${column} ELSE NULL END AS ${column}`,
        `typeof(${column}) AS __type_${column}`,
        `CASE WHEN typeof(${column})='text' THEN length(${column}) ELSE 0 END AS __length_${column}`,
    ]).join(',');
    const output = [], seen = new Set();
    for (let offset = 0; ; offset += PAGE) {
        const page = rows(await rawQuery(`SELECT ${projection} FROM (${sql}) LIMIT ${PAGE} OFFSET ${offset}`, values));
        if (page.length > PAGE) throw new Error('SQLite devolvió una página fuera de límite.');
        for (const entry of page) {
            if (entry[`__type_${key}`] !== 'text' || entry[`__length_${key}`] > PREFIX || typeof entry[key] !== 'string' || seen.has(entry[key])) throw new Error('La identidad de la fila SQLite requiere revisión.');
            seen.add(entry[key]);
            const result = {};
            for (const column of columns) {
                const type = entry[`__type_${column}`];
                if (!['text', 'integer', 'real', 'null'].includes(type)) throw new Error('Tipo SQLite no admitido; se conserva el original.');
                if (type !== 'text') { result[column] = entry[column]; continue; }
                const length = entry[`__length_${column}`];
                if (!Number.isSafeInteger(length) || length < 0 || typeof entry[column] !== 'string') throw new Error('Longitud SQLite no verificable.');
                const parts = [entry[column]];
                for (let position = PREFIX + 1; position <= length; position += CHUNK) {
                    const slice = rows(await rawQuery(`SELECT substr(${column},${position},${CHUNK}) AS chunk, length(substr(${column},${position},${CHUNK})) AS chunk_length FROM (${sql}) WHERE ${key} = ?`, [...values, entry[key]]));
                    if (slice.length !== 1 || typeof slice[0].chunk !== 'string' || slice[0].chunk_length !== Math.min(CHUNK, length - position + 1)) throw new Error('La fila cambió o quedó incompleta. Se conserva para recuperación.');
                    parts.push(slice[0].chunk);
                }
                result[column] = parts.join('');
            }
            output.push(result);
        }
        await assertStable();
        if (page.length < PAGE) return { values: output };
    }
}
